import { AlertCircle, RefreshCw, WifiOff, ServerCrash, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function ConnectionError({ onRetry, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6" data-testid="connection-error">
      <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-5">
        <WifiOff className="w-8 h-8 text-red-400" />
      </div>
      <h2 className="text-lg font-bold text-[#0D1830] mb-2">Connection Lost</h2>
      <p className="text-sm text-[#446E87] text-center max-w-md mb-6">
        {message || "Unable to reach the server. Please check your internet connection and try again."}
      </p>
      {onRetry && (
        <Button onClick={onRetry} className="bg-[#0D1830] text-white rounded-full text-sm font-semibold hover:opacity-90" data-testid="retry-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      )}
    </div>
  );
}

export function AnalysisError({ errorMessage, onRetry, projectName }) {
  return (
    <div className="border border-red-200 bg-red-50/50 rounded-xl p-6" data-testid="analysis-error">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
          <ServerCrash className="w-5 h-5 text-red-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-red-800 mb-1">Analysis Failed</h3>
          <p className="text-xs text-red-600 mb-3">
            The AI takeoff analysis for {projectName ? `"${projectName}"` : 'this project'} encountered an error.
          </p>
          {errorMessage && (
            <div className="bg-red-100/50 border border-red-200 rounded-lg px-3 py-2 mb-3">
              <p className="text-xs text-red-700 font-mono break-all">{errorMessage}</p>
            </div>
          )}
          <div className="flex items-center gap-3">
            {onRetry && (
              <Button size="sm" onClick={onRetry} className="bg-red-600 text-white rounded-full text-xs hover:bg-red-700" data-testid="retry-analysis-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                Retry Analysis
              </Button>
            )}
            <p className="text-[10px] text-red-400">Check that your files are valid PDFs and try again</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PageError({ onGoBack, onRetry }) {
  return (
    <div className="min-h-screen bg-[#F7FAFB] flex items-center justify-center" data-testid="page-error">
      <div className="text-center px-6 max-w-md">
        <div className="w-20 h-20 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-10 h-10 text-red-400" />
        </div>
        <h1 className="text-xl font-bold text-[#0D1830] mb-2">Something Went Wrong</h1>
        <p className="text-sm text-[#446E87] mb-6">
          We couldn't load this page. This could be due to a network issue or server error.
        </p>
        <div className="flex items-center justify-center gap-3">
          {onGoBack && (
            <Button variant="outline" onClick={onGoBack} className="rounded-full text-sm" data-testid="go-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          )}
          {onRetry && (
            <Button onClick={onRetry} className="bg-[#0D1830] text-white rounded-full text-sm font-semibold hover:opacity-90" data-testid="retry-page-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
